import { Box } from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import type { TabType } from 'modules/core/components/Table'
import { Tabs } from 'modules/core/components/Table'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import hasFeature from 'modules/core/lib/hasFeature'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'

import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import { useCustomPeriods } from 'modules/attendance/store/collections/customPeriods'
import { useEmployeeStats } from 'modules/attendance/store/collections/employeeStats'
import type { Status } from 'modules/attendance/store/models/Period'
import Period from 'modules/attendance/store/models/Period'

import type CustomPeriodForm from '../Layout/form'
import NoticeBox from './NoticeBox'
import CustomPeriodsTable from './_Table'
import stateToColor from './stateToColor'
import stateToLabel from './stateToLabel'

interface PanelByStateProps<TabIds> {
  customPeriods: Period[]
  filtersForm: FiltersForm
  customPeriodForm: CustomPeriodForm
  hasOvertimeTimeOff: boolean
  isSelected: boolean
  tab: TabType<TabIds>
  canConfirm: boolean
  clockInStatuses: ClockInStatus[]
  canSendEmailReminder: boolean
  showHourDistributionColumn: boolean
}

const PanelByState = ({
  customPeriods,
  filtersForm,
  customPeriodForm,
  hasOvertimeTimeOff,
  isSelected,
  tab,
  canConfirm,
  clockInStatuses,
  canSendEmailReminder,
  showHourDistributionColumn,
}: PanelByStateProps<Status>) => {
  return (
    <Tabs.Panel tab={tab} isSelected={isSelected}>
      <CustomPeriodsTable
        customPeriods={customPeriods}
        filtersForm={filtersForm}
        currentTabState={tab.id}
        canConfirm={canConfirm}
        customPeriodForm={customPeriodForm}
        hasOvertimeTimeOff={hasOvertimeTimeOff}
        clockInStatuses={clockInStatuses}
        canSendEmailReminder={canSendEmailReminder}
        showHourDistributionColumn={showHourDistributionColumn}
      />
    </Tabs.Panel>
  )
}

interface Props {
  filtersForm: FiltersForm
  customPeriodForm: CustomPeriodForm
  hasOvertimeTimeOff: boolean
  canApproveAttendance: boolean
  clockInStatuses: ClockInStatus[]
  canSendEmailReminder: boolean
  showHourDistributionColumn: boolean
  canManageLocation: boolean
}

interface StateCounters {
  [x: string | Status]: number
}

const Content = ({
  filtersForm,
  customPeriodForm,
  hasOvertimeTimeOff,
  canApproveAttendance,
  clockInStatuses,
  canSendEmailReminder,
  showHourDistributionColumn,
  canManageLocation,
}: Props) => {
  const collectionPeriods = useCustomPeriods()
  const employeeStats = useEmployeeStats()
  const { in_progress: inProgress, pending, approved } = Period.STATUS

  const shownPeriods = (): Period[] | undefined => {
    // NOTE: We skip the periods are not included in the current selection (customPeriodForm)
    // some users with pagination time_tracking_page_with_pagination enabled were experiencing duplicated
    // employees in their list.
    // The problem happens when user navigates fast in a daily view, some requests took longer
    // and we receive a response related to a previus date. The matches will filter
    // based on period startOn and endOn dates
    return collectionPeriods.filter(
      (period) =>
        period.policy.can('readPeriod') && customPeriodForm.matches(period.startOn, period.endOn)
    )
  }

  const shownPeriodsFiltered = shownPeriods() ?? []
  const groupedPeriods = groupBy(shownPeriodsFiltered, (period) => period.state) ?? []
  const customPeriodInProgress = customPeriodForm.isInProgress(moment())

  const tabsFromEmployeeStats = (): Array<TabType<Status>> => {
    const employeesByStateCounters = (): StateCounters => {
      if (customPeriodInProgress) {
        return {
          [inProgress]: employeeStats.inProgress.length,
        }
      }

      return {
        [pending]: employeeStats.pending.length,
        [approved]: employeeStats.approved.length,
      }
    }

    const enployeesStateCounters = Object.entries(employeesByStateCounters())

    return enployeesStateCounters.map(([key, counter]) => {
      const state = key as Status
      return {
        id: state,
        label: stateToLabel(state),
        color: stateToColor(state),
        counter: counter,
      }
    })
  }

  const tabsFromGroupedPeriods = (): Array<TabType<Status>> => {
    const groupedPeriodsKeys = Object.keys(groupedPeriods)

    return groupedPeriodsKeys.map((key) => {
      const state = key as Status
      return {
        id: state,
        label: stateToLabel(state),
        color: stateToColor(state),
        counter: groupedPeriods[state]?.length,
      }
    })
  }

  const periodsByState = () => {
    if (customPeriodInProgress) {
      return {
        [inProgress]: groupedPeriods[inProgress] ?? [],
      }
    } else {
      return {
        [pending]: groupedPeriods[pending] ?? [],
        [approved]: groupedPeriods[approved] ?? [],
      }
    }
  }

  const periodsState = periodsByState()
  const tabs = hasFeature('time_tracking_page_with_pagination')
    ? tabsFromEmployeeStats()
    : tabsFromGroupedPeriods()
  const { value: canManageShifts } = useAsyncCan('shift_management.create_shifts')

  return (
    <Box width="full">
      {canApproveAttendance && canManageShifts && (
        <NoticeBox canManageLocation={canManageLocation} />
      )}
      <ErrorBoundary>
        <Tabs tabs={tabs}>
          {({ tab, isSelected }) => (
            <PanelByState
              key={tab.id}
              tab={tab}
              isSelected={isSelected}
              filtersForm={filtersForm}
              customPeriodForm={customPeriodForm}
              customPeriods={periodsState[tab.id] ?? []}
              hasOvertimeTimeOff={hasOvertimeTimeOff}
              canConfirm={canApproveAttendance}
              clockInStatuses={clockInStatuses}
              canSendEmailReminder={canSendEmailReminder}
              showHourDistributionColumn={showHourDistributionColumn}
            />
          )}
        </Tabs>
      </ErrorBoundary>
    </Box>
  )
}

export default observer(Content)
